/**
 * Cross-platform audio capture format selection for {@link MediaRecorder}.
 *
 * Chrome / Firefox / Android WebView record WebM/Opus; iOS Safari (and the WKWebView used by a
 * Capacitor wrapper) cannot and only expose MP4/AAC. Picking the container from
 * {@link MediaRecorder.isTypeSupported} lets one codebase record on every platform, and exposing the
 * matching file extension keeps the S3 checkpoint object keys consistent with their real container.
 *
 * Safari (macOS and iOS) must use fragmented MP4 checkpoints — even when WebM is advertised, byte-stitching
 * WebM batches on Safari produces truncated transcripts. Both platforms upload a checkpoint about every 2 minutes.
 * Pause flushes whatever is still only in the browser, even when that tail is shorter than 2 minutes.
 */
export interface RecorderFormat {
  /** Value to pass to the {@link MediaRecorder} constructor; empty string means "let the browser decide". */
  mimeType: string;
  container: 'webm' | 'mp4' | 'ogg';
  /** File extension (no dot) for the upload object key, e.g. `chunks-1.webm` vs `chunks-1.mp4`. */
  fileExtension: string;
}

/** How often a checkpoint batch is posted, on every platform. */
export const CHECKPOINT_SAVE_INTERVAL_MS = 120_000;
/** Chrome/Firefox: local slice size. Eight slices fill one 2-minute checkpoint POST. */
export const WEBM_CHUNK_TIMESLICE_MS = 15_000;
/** Safari fMP4: one slice per checkpoint POST (avoid byte-stitching fragments), rotated every 2 minutes. */
export const MP4_CHECKPOINT_TIMESLICE_MS = CHECKPOINT_SAVE_INTERVAL_MS;
/** WebM slices combined into one checkpoint POST (8 × 15s = 2 minutes). */
export const WEBM_CHUNKS_PER_CHECKPOINT = CHECKPOINT_SAVE_INTERVAL_MS / WEBM_CHUNK_TIMESLICE_MS;

const MP4_RECORDER_CANDIDATES: RecorderFormat[] = [
  { mimeType: 'audio/mp4;codecs=mp4a.40.2', container: 'mp4', fileExtension: 'mp4' },
  { mimeType: 'audio/mp4', container: 'mp4', fileExtension: 'mp4' },
  { mimeType: 'audio/aac', container: 'mp4', fileExtension: 'm4a' },
];

/**
 * WebM/Opus is listed first so existing Chrome/Android behavior is byte-for-byte unchanged; MP4/AAC
 * variants cover iOS Safari / WKWebView, which is the only place the earlier hard-coded WebM failed.
 */
const RECORDER_FORMAT_CANDIDATES: RecorderFormat[] = [
  { mimeType: 'audio/webm;codecs=opus', container: 'webm', fileExtension: 'webm' },
  { mimeType: 'audio/webm', container: 'webm', fileExtension: 'webm' },
  ...MP4_RECORDER_CANDIDATES,
  { mimeType: 'audio/ogg;codecs=opus', container: 'ogg', fileExtension: 'ogg' },
];

/** True for Safari macOS/iOS (including WebKit without Chrome/Android in UA). */
export function isSafariBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(ua) || /iPhone|iPad|iPod/i.test(ua);
}

/** Safari must record fMP4 checkpoints — not WebM — for server assembly and transcript parity. */
export function shouldUseSafariMp4Recording(): boolean {
  return isSafariBrowser();
}

/** Map an already-known recorder mime type to its container/extension (e.g. when resuming a draft). */
export function recorderFormatForMimeType(mimeType: string | null | undefined): RecorderFormat {
  const m = (mimeType ?? '').toLowerCase();
  if (m.includes('webm')) {
    return { mimeType: mimeType as string, container: 'webm', fileExtension: 'webm' };
  }
  if (m.includes('ogg')) {
    return { mimeType: mimeType as string, container: 'ogg', fileExtension: 'ogg' };
  }
  if (m.includes('mp4') || m.includes('m4a') || m.includes('aac') || m.includes('mpeg')) {
    return {
      mimeType: mimeType as string,
      container: 'mp4',
      fileExtension: m.includes('m4a') ? 'm4a' : 'mp4',
    };
  }
  if (shouldUseSafariMp4Recording()) {
    return { mimeType: mimeType ?? 'audio/mp4', container: 'mp4', fileExtension: 'mp4' };
  }
  return { mimeType: mimeType ?? '', container: 'webm', fileExtension: 'webm' };
}

/** True when checkpoints must be uploaded as separate fMP4 fragments (Safari), not stitched WebM batches. */
export function usesFragmentedMp4Checkpoints(mimeType: string | null | undefined): boolean {
  if (shouldUseSafariMp4Recording()) {
    return true;
  }
  return recorderFormatForMimeType(mimeType).container === 'mp4';
}

/** MediaRecorder timeslice for periodic {@code dataavailable} / checkpoint uploads. */
export function checkpointTimesliceMs(mimeType: string | null | undefined): number {
  return usesFragmentedMp4Checkpoints(mimeType) ? MP4_CHECKPOINT_TIMESLICE_MS : WEBM_CHUNK_TIMESLICE_MS;
}

/** Slices combined into one checkpoint POST (1 for Safari fMP4, 8 for WebM — both about 2 minutes). */
export function chunksPerCheckpointUpload(mimeType: string | null | undefined): number {
  return usesFragmentedMp4Checkpoints(mimeType) ? 1 : WEBM_CHUNKS_PER_CHECKPOINT;
}

/** Mime to store on a draft restored from server chunks of this extension. */
export function mimeTypeForCheckpointExtension(fileExtension: string | null | undefined): string {
  const ext = (fileExtension ?? '').toLowerCase();
  if (ext === 'mp4' || ext === 'm4a') {
    return 'audio/mp4';
  }
  return 'audio/webm;codecs=opus';
}

/**
 * True when this browser records the same container as the chunks already on the server.
 * A mismatched continue would append a different container onto the same recording id.
 */
export function browserCanContinueCheckpointContainer(fileExtension: string | null | undefined): boolean {
  const ext = (fileExtension ?? '').toLowerCase();
  if (ext !== 'webm' && ext !== 'mp4' && ext !== 'm4a') {
    return false;
  }
  const serverMp4 = ext === 'mp4' || ext === 'm4a';
  return serverMp4 === shouldUseSafariMp4Recording();
}

/**
 * First logical chunk index not yet confirmed on S3.
 * When {@code lastUploadedChunkIndex} is set, that cursor wins. Batch-count math uses a different
 * slice size for WebM (8) and fMP4 (1), so taking the smaller of the two re-requests slices that
 * were already posted and deleted from local storage.
 */
export function firstChunkIndexPendingUpload(
  manifest: {
    lastChunkIndex: number;
    lastUploadedChunkIndex?: number;
    nextCheckpointBatchNumber?: number;
    recorderMimeType?: string;
  },
  mimeType?: string | null
): number {
  const perBatch = chunksPerCheckpointUpload(manifest.recorderMimeType ?? mimeType);
  const fromBatches = Math.max(0, ((manifest.nextCheckpointBatchNumber ?? 1) - 1) * perBatch);
  const localChunkCount = Math.max(0, manifest.lastChunkIndex + 1);
  if (typeof manifest.lastUploadedChunkIndex === 'number') {
    return Math.min(localChunkCount, manifest.lastUploadedChunkIndex + 1);
  }
  return Math.min(localChunkCount, fromBatches);
}

/**
 * Choose the best capture format this browser actually supports. Returns the first supported candidate;
 * if {@link MediaRecorder.isTypeSupported} is unavailable, returns an empty mime type so the
 * {@link MediaRecorder} default is used (the caller should then read back {@code mediaRecorder.mimeType}).
 */
export function pickSupportedRecorderFormat(): RecorderFormat {
  const canProbe =
    typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function';
  if (canProbe) {
    for (const candidate of RECORDER_FORMAT_CANDIDATES) {
      try {
        if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
          return { ...candidate };
        }
      } catch {
        // isTypeSupported can throw on some engines for odd strings; keep probing.
      }
    }
  }
  return { mimeType: '', container: 'webm', fileExtension: 'webm' };
}

/**
 * Platform-aware format selection: Safari → MP4 only (never WebM), others → {@link pickSupportedRecorderFormat}.
 */
export function pickRecorderFormatForPlatform(): RecorderFormat {
  if (!shouldUseSafariMp4Recording()) {
    return pickSupportedRecorderFormat();
  }
  const canProbe =
    typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function';
  if (canProbe) {
    for (const candidate of MP4_RECORDER_CANDIDATES) {
      try {
        if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
          return { ...candidate };
        }
      } catch {
        // keep probing
      }
    }
  }
  return { mimeType: '', container: 'mp4', fileExtension: 'mp4' };
}

/** File extension (no dot) for an upload object key derived from a recorder mime type. */
export function fileExtensionForMimeType(mimeType: string | null | undefined): string {
  return recorderFormatForMimeType(mimeType).fileExtension;
}
